/**
 * ShimmerControls — Shimmer light-streak effect configuration panel
 *
 * Two modes:
 * - 'medium': Only shows enabled toggle (hover shimmer with defaults)
 * - 'advanced': Full control over all shimmer parameters
 */

import React, { useCallback } from 'react';
import {
  Box,
  Switch,
  Typography,
  FormControlLabel,
  Select,
  MenuItem,
  Slider,
} from '@mui/material';
import type { ShimmerConfig } from '@segmently/schema-models/v2';
import { ColorInput } from './ColorInput';
import { DEFAULT_SHIMMER_HOVER } from '../../../utils/gradientUtils';

export interface ShimmerControlsProps {
  shimmer: ShimmerConfig | null | undefined;
  onChange: (shimmer: ShimmerConfig | null) => void;
  /** 'medium' shows only toggle, 'advanced' shows all controls */
  mode: 'medium' | 'advanced';
  disabled?: boolean;
}

export const ShimmerControls: React.FC<ShimmerControlsProps> = ({
  shimmer,
  onChange,
  mode,
  disabled = false,
}) => {
  const isEnabled = shimmer?.enabled === true;

  const handleToggle = useCallback(
    (_: unknown, checked: boolean) => {
      if (checked) {
        onChange({ ...DEFAULT_SHIMMER_HOVER });
      } else {
        onChange(null);
      }
    },
    [onChange],
  );

  const updateField = useCallback(
    <K extends keyof ShimmerConfig>(field: K, value: ShimmerConfig[K]) => {
      onChange({ ...(shimmer ?? DEFAULT_SHIMMER_HOVER), enabled: true, [field]: value });
    },
    [shimmer, onChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={isEnabled}
            onChange={handleToggle}
            disabled={disabled}
          />
        }
        label={
          <Typography variant="caption" fontWeight={500}>
            Shimmer
          </Typography>
        }
        sx={{ ml: 0 }}
      />

      {isEnabled && mode === 'advanced' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pl: 1 }}>
          {/* Trigger */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Trigger
            </Typography>
            <Select
              size="small"
              value={shimmer?.trigger ?? 'hover'}
              onChange={(e) => updateField('trigger', e.target.value as ShimmerConfig['trigger'])}
              disabled={disabled}
              sx={{ flex: 1, fontSize: 12, '& .MuiSelect-select': { py: 0.5 } }}
            >
              <MenuItem value="hover" sx={{ fontSize: 12 }}>Hover</MenuItem>
              <MenuItem value="timer" sx={{ fontSize: 12 }}>Timer</MenuItem>
              <MenuItem value="none" sx={{ fontSize: 12 }}>None</MenuItem>
            </Select>
          </Box>

          {/* Color */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Color
            </Typography>
            <ColorInput
              value={shimmer?.color ?? 'rgba(255, 255, 255, 0.15)'}
              onChange={(color) => updateField('color', color as string)}
              disabled={disabled}
            />
          </Box>

          {/* Width */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Width
            </Typography>
            <Slider
              size="small"
              value={Math.round((shimmer?.width ?? 0.5) * 100)}
              onChange={(_, val) => updateField('width', (val as number) / 100)}
              min={10}
              max={100}
              disabled={disabled}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
              {Math.round((shimmer?.width ?? 0.5) * 100)}%
            </Typography>
          </Box>

          {/* Skew */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Skew
            </Typography>
            <Slider
              size="small"
              value={shimmer?.skew ?? -20}
              onChange={(_, val) => updateField('skew', val as number)}
              min={-45}
              max={45}
              disabled={disabled}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
              {shimmer?.skew ?? -20}&deg;
            </Typography>
          </Box>

          {/* Duration */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 52 }}>
              Duration
            </Typography>
            <Slider
              size="small"
              value={shimmer?.duration ?? 0.6}
              onChange={(_, val) => updateField('duration', val as number)}
              min={0.1}
              max={3.0}
              step={0.1}
              disabled={disabled}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
              {(shimmer?.duration ?? 0.6).toFixed(1)}s
            </Typography>
          </Box>

          {/* Cycle (only for timer trigger) */}
          {shimmer?.trigger === 'timer' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ minWidth: 52 }}>
                Cycle
              </Typography>
              <Slider
                size="small"
                value={shimmer?.cycle ?? 4.0}
                onChange={(_, val) => updateField('cycle', val as number)}
                min={1.0}
                max={10.0}
                step={0.5}
                disabled={disabled}
                sx={{ flex: 1 }}
              />
              <Typography variant="caption" sx={{ minWidth: 28, textAlign: 'right' }}>
                {(shimmer?.cycle ?? 4.0).toFixed(1)}s
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
